<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watchEffect } from 'vue'

import cloudDefaultImage from './assets/smartwork-cloud.webp'
import enjeuxDefaultImage from './assets/smartwork-enjeux.webp'
import laptopDefaultImage from './assets/smartwork-laptop-interface.webp'
import nostressDefaultImage from './assets/smartwork-no-stress.webp'
import serveursecuriteshieldcheckDefaultImage from './assets/smartwork-securite-shield-check.webp'
import serveurecofeuilleDefaultImage from './assets/smartwork-serveur-eco-feuille.webp'
import serveurecoplanteDefaultImage from './assets/smartwork-serveur-eco-plante.webp'
import serverDefaultImage from './assets/smartwork-serveur.webp'
import smartphoneDefaultImage from './assets/smartwork-smartphone-interface.webp'
import LegalPage from './components/LegalPage.vue'
import { benefices } from './data/benefices'
import { defis } from './data/defis'
import { environnementStats } from './data/environnement'
import { features } from './data/hero'
import { engages } from './data/infrastructure'
import { infogerances } from './data/infogerance'
import { integrations } from './data/integration'
import { legalPageLinks, legalPages } from './data/legalPages'
import { services } from './data/services'
import { soluts } from './data/soluts'

const heroImageDesktop = import.meta.env.VITE_HERO_DESKTOP || '/smartwork-hero.png'
const heroImageMobile = import.meta.env.VITE_HERO_MOBILE || '/smartwork-hero-smartphone.png'
const heroImageAlt = import.meta.env.VITE_HERO_ALT || 'SmartWork : votre IA souveraine, hébergée en France, au service de votre entreprise.'

const cloudImage = import.meta.env.VITE_CLOUD_IMAGE || cloudDefaultImage
const cloudImageAlt = import.meta.env.VITE_CLOUD_ALT || 'SmartWork des serveurs français et européens qui hébergent votre IA souveraine.'

const serverImage = import.meta.env.VITE_SERVER_IMAGE || serverDefaultImage
const serverImageAlt = import.meta.env.VITE_SERVER_ALT || 'SmartWork une IA d’entreprise hébergée sur des serveurs français, sécurisés et supervisés 24/7.'

const laptopImage = import.meta.env.VITE_LAPTOP_IMAGE || laptopDefaultImage
const laptopImageAlt = import.meta.env.VITE_LAPTOP_ALT || 'SmartWork un ordinateur portable affichant une interface de chat IA souveraine, accessible depuis le navigateur.'

const smartphoneImage = import.meta.env.VITE_SMARTPHONE_IMAGE || smartphoneDefaultImage
const smartphoneImageAlt = import.meta.env.VITE_SMARTPHONE_ALT || 'SmartWork un smartphone affichant l’interface de votre IA souveraine, disponible partout, protégée en France.'

const nostressImage = import.meta.env.VITE_NOSTRESS_IMAGE || nostressDefaultImage
const nostressImageAlt = import.meta.env.VITE_NOSTRESS_ALT || 'SmartWork sans stress, sans soucis, sans tracas. Une solution simple et efficace pour gérer vos données.'

const serveurecofeuilleImage = import.meta.env.VITE_SERVEUR_ECOFEUILLE_IMAGE || serveurecofeuilleDefaultImage
const serveurecofeuilleImageAlt = import.meta.env.VITE_SERVEUR_ECOFEUILLE_ALT || 'Feuille verte avec gouttes d’eau.'

const serveurecoplanteImage = import.meta.env.VITE_SERVEUR_ECOPLANTE_IMAGE || serveurecoplanteDefaultImage
const serveurecoplanteImageAlt = import.meta.env.VITE_SERVEUR_ECOPLANTE_ALT || 'Plante verte en pot.'

const serveursecuriteshieldcheckImage = import.meta.env.VITE_SERVEUR_SECURITE_SHIELD_CHECK_IMAGE || serveursecuriteshieldcheckDefaultImage
const serveursecuriteshieldcheckImageAlt = import.meta.env.VITE_SERVEUR_SECURITE_SHIELD_CHECK_ALT || 'SmartWork un serveur sécurisé, conforme aux normes françaises et européennes, un bouclier de sécurité sur-mesure.'

const contactImage = import.meta.env.VITE_CONTACT_IMAGE || enjeuxDefaultImage
const contactImageAlt = import.meta.env.VITE_CONTACT_IMAGE_ALT || 'SmartWork analyse vos enjeux pour réduire les risques.'

const contactForm = ref({
  name: '',
  email: '',
  phone: '',
  company: '',
  message: '',
  consent: false,
  gotcha: '',
})
const contactStatus = ref('')
const contactFeedback = ref('')
const isContactSubmitting = ref(false)
const contactFormMountTime = ref(0)
const contactSubmitEndpoint = '/api/contact/submit'

// --- Prise de rendez-vous (option B) ---------------------------------------
const bookingForm = ref({
  name: '',
  email: '',
  phone: '',
  company: '',
  date: '',
  slot: '',
  duration: 30,
  gotcha: '',
})
const bookingStatus = ref('')
const bookingFeedback = ref('')
const isBookingSubmitting = ref(false)
const bookingFormMountTime = ref(0)
const bookingAvailabilityEndpoint = '/api/booking/availability'
const bookingSubmitEndpoint = '/api/booking/submit'
const bookingDays = ref([])
const bookingSlots = ['09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30']
const bookingDurations = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
]
const WEEKDAYS_FR = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi']
const MONTHS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']

const formatBookingDate = (iso) => {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  const dt = new Date(Date.UTC(y, m - 1, d))
  return `${WEEKDAYS_FR[dt.getUTCDay()]} ${d} ${MONTHS_FR[m - 1]}`
}

const loadBookingAvailability = async () => {
  try {
    const response = await fetch(bookingAvailabilityEndpoint)
    if (!response.ok) return
    const data = await response.json()
    if (Array.isArray(data.days)) {
      bookingDays.value = data.days
    }
  } catch {
    // indisponibilité silencieuse : le formulaire reste utilisable via le contact
  }
}

const handleBookingSubmit = async () => {
  if (isBookingSubmitting.value) return

  if (bookingForm.value.gotcha) return

  if (!bookingForm.value.name || !bookingForm.value.email || !bookingForm.value.phone || !bookingForm.value.date || !bookingForm.value.slot) {
    bookingStatus.value = 'error'
    bookingFeedback.value = 'Merci de renseigner votre nom, votre email, votre téléphone et de choisir un créneau.'
    return
  }

  isBookingSubmitting.value = true
  bookingStatus.value = ''
  bookingFeedback.value = ''

  const payload = {
    name: bookingForm.value.name,
    email: bookingForm.value.email,
    phone: bookingForm.value.phone,
    company: bookingForm.value.company,
    date: bookingForm.value.date,
    slot: bookingForm.value.slot,
    duration_min: Number(bookingForm.value.duration),
    _gotcha: bookingForm.value.gotcha,
    _fill_ms: Date.now() - (bookingFormMountTime.value || Date.now() - 30000),
  }

  try {
    const response = await fetch(bookingSubmitEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

    if (!response.ok) throw new Error('request_failed')

    bookingStatus.value = 'success'
    const [y, m, d] = bookingForm.value.date.split('-').map(Number)
    const dt = new Date(Date.UTC(y, m - 1, d))
    bookingFeedback.value = `Merci ! Votre demande de rendez-vous ${WEEKDAYS_FR[dt.getUTCDay()]} ${d} ${MONTHS_FR[m - 1]} à ${bookingForm.value.slot} a été enregistrée. Nous vous confirmons par email.`

    bookingForm.value = { name: '', email: '', phone: '', company: '', date: '', slot: '', duration: 30, gotcha: '' }
    bookingFormMountTime.value = Date.now()
  } catch {
    bookingStatus.value = 'error'
    bookingFeedback.value = 'Une erreur est survenue. Merci de réessayer ou de nous écrire à contact@smartwork.fr.'
  } finally {
    isBookingSubmitting.value = false
  }
}

const backgroundSvg = import.meta.env.VITE_HERO_BACKGROUND || ''
const defaultPageTitle = 'SmartWork — IA souveraine pour votre entreprise, un Web plus intelligent pour tous.'
const defaultMetaDescription = 'SmartWork déploie votre IA souveraine : une IA générative hébergée en France, conforme au RGPD, sans réutilisation de vos données, accompagnée par des experts 24/7.'

const normalizePath = (path) => {
  if (!path || path === '/') {
    return '/'
  }

  return path.endsWith('/') ? path.slice(0, -1) : path
}

const routePath = ref(typeof window === 'undefined' ? '/' : normalizePath(window.location.pathname))
const currentLegalPage = computed(() => legalPages[routePath.value] || null)
const isLegalLinkActive = (path) => currentLegalPage.value?.path === normalizePath(path)

const primaryNavigationItems = [
  {
    targetId: 'section-hero',
    href: '/#section-hero',
    label: 'Accueil',
    iconFamily: 'fa-solid',
    icon: 'house',
    variant: 'home',
  },
  {
    targetId: 'section-infrastructure',
    href: '/#section-infrastructure',
    label: 'Section 01 - Infrastructure',
    shortLabel: '01',
  },
  {
    targetId: 'section-soluts',
    href: '/#section-soluts',
    label: 'Section 02 - Solutions',
    shortLabel: '02',
  },
  {
    targetId: 'section-services',
    href: '/#section-services',
    label: 'Section 03 - Services',
    shortLabel: '03',
  },
  {
    targetId: 'section-infogerance',
    href: '/#section-infogerance',
    label: 'Section 04 - Exploitation IA',
    shortLabel: '04',
  },
  {
    targetId: 'section-integration',
    href: '/#section-integration',
    label: 'Section 05 - Plateforme IA',
    shortLabel: '05',
  },
  {
    targetId: 'section-defis',
    href: '/#section-defis',
    label: 'Section 06 - Défis',
    shortLabel: '06',
  },
  {
    targetId: 'section-benefices',
    href: '/#section-benefices',
    label: 'Section 07 - Bénéfices',
    shortLabel: '07',
  },
  {
    targetId: 'section-environnement',
    href: '/#section-environnement',
    label: 'Section 08 - Environnement',
    shortLabel: '08',
  },
  {
    targetId: 'section-contact',
    href: '/#section-contact',
    label: 'Contact',
    iconFamily: 'fa-solid',
    icon: 'at',
    variant: 'contact',
  },
]

const navigationTargetIds = new Set(primaryNavigationItems.map(({ targetId }) => targetId))
const activeSectionId = ref(routePath.value === '/' ? 'section-hero' : '')

const getHashTargetId = (hash) => {
  if (!hash) {
    return ''
  }

  try {
    return decodeURIComponent(hash.slice(1))
  } catch {
    return hash.slice(1)
  }
}

const scrollToElementById = async (targetId, behavior = 'smooth') => {
  if (typeof document === 'undefined' || !targetId) {
    return false
  }

  await nextTick()

  const targetElement = document.getElementById(targetId)

  if (!targetElement) {
    return false
  }

  if (navigationTargetIds.has(targetId)) {
    activeSectionId.value = targetId
  }

  targetElement.scrollIntoView({ block: 'start', behavior })
  scheduleParallaxUpdate()
  return true
}

let activeSectionFrame = 0
let parallaxFrame = 0

const clampScrollProgress = (value) => Math.min(Math.max(value, 0), 1)

const getParallaxMeasure = (figure, propertyName, fallback = 0) => {
  const value = getComputedStyle(figure).getPropertyValue(propertyName).trim()
  const measure = Number.parseFloat(value)

  if (!Number.isFinite(measure)) {
    return fallback
  }

  if (value.endsWith('%')) {
    return window.innerHeight * measure / 100
  }

  return measure
}

const updateActiveSectionFromViewport = () => {
  activeSectionFrame = 0

  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return
  }

  if (routePath.value !== '/') {
    activeSectionId.value = ''
    return
  }

  const anchorY = Math.min(window.innerHeight * 0.5, window.innerHeight - 120)
  let nextActiveId = primaryNavigationItems[0].targetId

  primaryNavigationItems.forEach((item) => {
    const section = document.getElementById(item.targetId)

    if (!section) {
      return
    }

    if (section.getBoundingClientRect().top <= anchorY) {
      nextActiveId = item.targetId
    }
  })

  activeSectionId.value = nextActiveId
}

const scheduleActiveSectionUpdate = () => {
  if (typeof window === 'undefined' || activeSectionFrame) {
    return
  }

  activeSectionFrame = window.requestAnimationFrame(updateActiveSectionFromViewport)
}

const updateParallaxImages = () => {
  parallaxFrame = 0

  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return
  }

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const parallaxFigures = document.querySelectorAll('.img-parallax')

  parallaxFigures.forEach((figure) => {
    if (!(figure instanceof HTMLElement)) {
      return
    }

    if (prefersReducedMotion) {
      figure.style.setProperty('--parallax-y', '0px')
      figure.style.setProperty('--parallax-opacity', '1')
      return
    }

    const image = figure.querySelector('img')

    if (!(image instanceof HTMLElement)) {
      return
    }

    const imageRect = image.getBoundingClientRect()
    const startOffset = getParallaxMeasure(figure, '--parallax-start-offset')
    const rect = {
      top: imageRect.top - startOffset,
      bottom: imageRect.bottom - startOffset,
    }
    const revealDelay = getParallaxMeasure(
      figure,
      '--parallax-reveal-delay',
      Math.min(Math.max(window.innerHeight * 0.18, 140), 260),
    )
    const revealDistance = getParallaxMeasure(
      figure,
      '--parallax-reveal-distance',
      Math.min(Math.max(window.innerHeight * 0.14, 120), 220),
    )
    const enterProgress = clampScrollProgress((window.innerHeight - revealDelay - rect.top) / revealDistance)
    const exitProgress = clampScrollProgress(rect.bottom / revealDistance)
    const progress = Math.min(enterProgress, exitProgress)
    const y = (1 - progress) * 70

    figure.style.setProperty('--parallax-y', `${y.toFixed(2)}px`)
    figure.style.setProperty('--parallax-opacity', progress.toFixed(3))
  })
}

const scheduleParallaxUpdate = () => {
  if (typeof window === 'undefined' || parallaxFrame) {
    return
  }

  parallaxFrame = window.requestAnimationFrame(updateParallaxImages)
}

const syncRouteFromLocation = () => {
  routePath.value = normalizePath(window.location.pathname)

  const targetId = getHashTargetId(window.location.hash)

  if (routePath.value === '/' && targetId) {
    void scrollToElementById(targetId, 'auto')
    return
  }

  scheduleActiveSectionUpdate()
  scheduleParallaxUpdate()
}

const handleAppLinkClick = async (event) => {
  if (typeof window === 'undefined') {
    return
  }

  if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return
  }

  const target = event.target

  if (!(target instanceof Element)) {
    return
  }

  const link = target.closest('a')
  const href = link?.getAttribute('href')

  if (!href || href.startsWith('mailto:') || href.startsWith('tel:')) {
    return
  }

  if (link.target && link.target !== '_self') {
    return
  }

  const url = new URL(href, window.location.href)

  if (url.origin !== window.location.origin) {
    return
  }

  event.preventDefault()

  const nextUrl = `${url.pathname}${url.search}${url.hash}`
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`

  if (nextUrl !== currentUrl) {
    window.history.pushState({}, '', nextUrl)
  }

  routePath.value = normalizePath(url.pathname)

  const targetId = getHashTargetId(url.hash)

  if (navigationTargetIds.has(targetId)) {
    activeSectionId.value = targetId
  }

  if (targetId && await scrollToElementById(targetId)) {
    return
  }

  await nextTick()
  window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  scheduleActiveSectionUpdate()
  scheduleParallaxUpdate()
}

const handleContactSubmit = async () => {
  if (isContactSubmitting.value) {
    return
  }

  if (contactForm.value.gotcha) {
    return
  }

  if (!contactForm.value.consent) {
    contactStatus.value = 'error'
    contactFeedback.value = 'Merci de confirmer votre accord avant l’envoi.'
    return
  }

  if (!contactForm.value.name || !contactForm.value.email || !contactForm.value.message) {
    contactStatus.value = 'error'
    contactFeedback.value = 'Merci de renseigner votre nom, votre email et votre message.'
    return
  }

  isContactSubmitting.value = true
  contactStatus.value = ''
  contactFeedback.value = ''

  const payload = {
    name: contactForm.value.name,
    email: contactForm.value.email,
    phone: contactForm.value.phone,
    company: contactForm.value.company,
    message: contactForm.value.message,
    _gotcha: contactForm.value.gotcha,
    _fill_ms: Date.now() - contactFormMountTime.value,
  }

  try {
    const response = await fetch(contactSubmitEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      throw new Error('request_failed')
    }

    contactStatus.value = 'success'
    contactFeedback.value = 'Merci ! Votre demande a bien été envoyée, notre équipe vous répond rapidement.'

    contactForm.value = {
      name: '',
      email: '',
      phone: '',
      company: '',
      message: '',
      consent: false,
      gotcha: '',
    }
  } catch {
    contactStatus.value = 'error'
    contactFeedback.value = 'Une erreur est survenue pendant l’envoi. Merci de réessayer ou de nous écrire à contact@smartwork.fr.'
  } finally {
    isContactSubmitting.value = false
  }
}

onMounted(() => {
  window.addEventListener('popstate', syncRouteFromLocation)
  window.addEventListener('hashchange', syncRouteFromLocation)
  window.addEventListener('scroll', scheduleActiveSectionUpdate, { passive: true })
  window.addEventListener('scroll', scheduleParallaxUpdate, { passive: true })
  window.addEventListener('scrollend', scheduleParallaxUpdate)
  window.addEventListener('resize', scheduleActiveSectionUpdate)
  window.addEventListener('resize', scheduleParallaxUpdate)
  contactFormMountTime.value = Date.now()
  bookingFormMountTime.value = Date.now()
  void loadBookingAvailability()

  const targetId = getHashTargetId(window.location.hash)

  if (routePath.value === '/' && targetId) {
    void scrollToElementById(targetId, 'auto')
  } else {
    scheduleActiveSectionUpdate()
    scheduleParallaxUpdate()
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('popstate', syncRouteFromLocation)
  window.removeEventListener('hashchange', syncRouteFromLocation)
  window.removeEventListener('scroll', scheduleActiveSectionUpdate)
  window.removeEventListener('scroll', scheduleParallaxUpdate)
  window.removeEventListener('scrollend', scheduleParallaxUpdate)
  window.removeEventListener('resize', scheduleActiveSectionUpdate)
  window.removeEventListener('resize', scheduleParallaxUpdate)

  if (activeSectionFrame) {
    window.cancelAnimationFrame(activeSectionFrame)
  }

  if (parallaxFrame) {
    window.cancelAnimationFrame(parallaxFrame)
  }
})

watchEffect(() => {
  if (typeof document === 'undefined') {
    return
  }

  const page = currentLegalPage.value
  document.title = page?.metaTitle || defaultPageTitle

  const description = document.querySelector('meta[name="description"]')

  if (description) {
    description.setAttribute('content', page?.metaDescription || defaultMetaDescription)
  }
})

watchEffect(() => {
  if (!currentLegalPage.value && !contactFormMountTime.value) {
    contactFormMountTime.value = Date.now()
  }
})

</script>

<template>
  <div class="app-shell" @click="handleAppLinkClick">
    <LegalPage v-if="currentLegalPage" :page="currentLegalPage" />
    <main v-else class="mainsection">
      <!-- <div class="landing-page__background" aria-hidden="true">
      <img v-if="backgroundSvg" class="landing-page__background-svg" :src="backgroundSvg" :alt="heroImageAlt">
    </div> -->
      <!-- HERO SECTION -->
      <section id="section-hero" class="hero mainsection__hero">
        <div class="hero__content">
          <div class="hero__copy">
            <h1 class="hero__title">
              <span class="hero__title-accent">IA</span> souveraine pour votre entreprise, un
              <span class="hero__title-web"><span class="hero__title-accent">W</span>eb</span> plus intelligent pour
              tous.
            </h1>
            <p class="hero__lede">
              SmartWork déploie votre intelligence artificielle sur une infrastructure <span
                class="hero__lede-accent ">française, sécurisée et conforme</span> — vos données ne quittent jamais
              l’Europe, vos équipes gagnent en puissance.
            </p>
          </div>

          <div class="hero__visual">
            <img
              :src="heroImageMobile"
              :srcset="`${heroImageMobile} 830w, ${heroImageDesktop} 1279w`"
              sizes="(max-width: 576px) 100vw, (max-width: 767px) 422px, (max-width: 991px) min(96vw, 860px), (max-width: 1279px) min(92vw, 1080px), 1279px"
              :alt="heroImageAlt"
              width="830"
              height="516"
              fetchpriority="high"
              decoding="async"
            >
          </div>
        </div>
        <h2 class="hero__brand" aria-label="SmartWork">
          <span>Smart</span>
          <span class="hero__brand-accent">W</span>
          <span>ork</span>
        </h2>
        <ul class="hero__trust">
          <li v-for="feature in features" :key="feature.key" class="hero__trust-item">
            <span class="hero__trust-icon" aria-hidden="true">
              <i :class="[feature.iconFamily, `fa-${feature.icon}`]"></i>
            </span>

            <div class="hero__trust-text">
              <h3 class="hero__trust-title">{{ feature.title }}</h3>
              <p class="hero__trust-subtitle">{{ feature.subtitle }}</p>
            </div>
          </li>
        </ul>
      </section>
      <header class="section-site-header" aria-label="En-tête SmartWork">
        <div class="section-site-header__inner">
          <a class="section-site-header__brand" href="/#section-hero" aria-label="SmartWork">
            <span>Smart</span><span class="section-site-header__brand-accent">W</span><span>ork</span>
          </a>

          <a class="section-site-header__cta" href="/#section-contact">
            <span class="section-site-header__cta-text section-site-header__cta-text--full">Échanger avec un
              expert</span>
            <span class="section-site-header__cta-text section-site-header__cta-text--short">Échanger</span>
            <i class="fa-solid fa-arrow-right" aria-hidden="true"></i>
          </a>
        </div>
      </header>
      <!-- 01 INFRASTRUCTURE SECTION -->
      <section id="section-infrastructure" class="section infra mainsection__infra">
        <div class="section-content infra__content">
          <div class="infra__intro">
            <span class="infra__number section__number">01</span>
            <h2 class="infra__title">Une IA souveraine, des <span
                class="infra__title-accent">engagements</span>
              concrets pour votre entreprise.</h2>
            <figure class="img-parallax"><img class="infra__cloud" :src="cloudImage" :alt="cloudImageAlt" width="870" height="496" loading="lazy" decoding="async"></figure>
          </div>
          <ul class="section-list infra__engages-list">
            <li v-for="engage in engages" :key="engage.key" class="section-list__item infra__engage-item">
              <span class="section-list__item-icon infra__engage-icon" aria-hidden="true">
                <i :class="[engage.iconFamily, `fa-${engage.icon}`]"></i>
              </span>
              <h3 class="infra__engage-title">{{ engage.title }}</h3>
              <p class="infra__engage-text">{{ engage.text }}</p>
            </li>
          </ul>
        </div>
      </section>
      <!-- 02 SOLUTS SECTION -->
      <section id="section-soluts" class="section soluts mainsection__soluts">
        <figure class="img-parallax"><img class="soluts__server" :src="serverImage" :alt="serverImageAlt" loading="lazy" decoding="async"></figure>

        <div class="section-content soluts__content">
          <div class="section-col section-col__header">
            <header class="section_header soluts__header">
              <span class="soluts__number section__number">02</span>
              <h3>Nos solutions</h3>
              <h2 class="soluts__title">Une IA <span class="soluts__title-accent">hébergée en France</span>, pour travailler en toute confiance.</h2>
            </header>
            <p>Assistants IA métier, sécurité, conformité, accompagnement : SmartWork exploite pour vous une plateforme d’IA souveraine, sans dépendance aux acteurs extra-européens.</p>
          </div>

          <ul class="section-list soluts__solutions-list">
            <li v-for="solut in soluts" :key="solut.key" class="section-list__item soluts__solution-item">
              <span class="section-list__item-icon soluts__solution-icon" aria-hidden="true">
                <i :class="[solut.iconFamily, `fa-${solut.icon}`]"></i>
              </span>
              <h3 class="soluts__solution-title">{{ solut.title }}</h3>
              <p class="soluts__solution-text">{{ solut.text }}</p>
            </li>
          </ul>
        </div>
      </section>
      <!-- 03 SERVICES SECTION -->
      <section id="section-services" class="section services mainsection__services">
        <div class="section-content services__content">
          <div class="section-col section-col__header">
            <header class="section_header services__header">
              <span class="services__number section__number">03</span>
              <h3>À qui s’adresse notre service ?</h3>
              <h2 class="services__title">Une IA souveraine pour <span class="services__title-accent">toutes
                  vos équipes.</span>
              </h2>
            </header>
            <p>Du premier assistant rédactionnel aux cas d’usage avancés, SmartWork adapte sa plateforme IA à vos métiers,
              vos procédures et votre niveau de maturité.</p>
          </div>

          <ul class="section-list services__services-list">
            <li v-for="service in services" :key="service.key" class="section-list__item services__solution-item">
              <figure class="section-list__item-image services__services-icon">
                <img :src="service.image" :class="[`img-${service.icon}`]" :alt="service.imageAlt" loading="lazy" decoding="async" />
              </figure>
              <h3 class="services__solution-title">{{ service.title }} <span>{{ service.subtitle }}</span></h3>
              <p class="services__solution-text">{{ service.text }}</p>
            </li>
          </ul>
        </div>
      </section>
      <!-- 04 INFOGÉRANCE SECTION -->
      <section id="section-infogerance" class="section infogerance mainsection__infogerance">
        <div class="section-content infogerance__content">
          <div class="section-col section-col__header">
            <header class="section_header infogerance__header">
              <span class="infogerance__number section__number">04</span>
              <h3>Exploitation IA</h3>
              <h2 class="infogerance__title">Une approche claire, rigoureuse et
                <span class="infogerance__title-accent">humaine</span>.
              </h2>
            </header>
            <p>Assistance ≠ infogérance&nbsp;: SmartWork prend en charge l’ensemble du cycle de vie de votre IA&nbsp;: installation, mises à jour, sauvegardes, supervision, montée en charge et évolution des modèles — vous vous concentrez sur vos usages.</p>
          </div>

          <ul class="section-list infogerance__services-list">
            <li v-for="infogerance in infogerances" :key="infogerance.key"
              class="section-list__item infogerance__solution-item">
              <span class="section-list__item-icon infogerance__solution-icon" aria-hidden="true">
                <i :class="[infogerance.iconFamily, `fa-${infogerance.icon}`]"></i>
              </span>
              <h3 class="infogerance__solution-title">{{ infogerance.title }}</h3>
              <p class="infogerance__solution-text">{{ infogerance.text }}</p>
            </li>
          </ul>
        </div>
        <figure class="img-parallax infogerance__visual-frame">
          <img class="infogerance__visual" :src="laptopImage" :alt="laptopImageAlt" loading="lazy" decoding="async">
        </figure>
      </section>
      <!-- 05 INTÉGRATION SECTION -->
      <section id="section-integration" class="section integration mainsection__integration">
        <div class="section-content integration__content">
          <header class="section_header integration__header">
            <span class="integration__number section__number">05</span>
            <h3>Plateforme IA</h3>
            <h2 class="integration__title">Une proposition intégrée, alliant
              <span class="integration__title-accent">rapidité</span>, <span
                class="integration__title-accent">sécurité</span> et <span
                class="integration__title-accent">adaptabilité</span>.
            </h2>
          </header>
          <p>Une plateforme IA complète et intégrée&nbsp;: interface web, catalogue de modèles, assistants métier et sauvegardes — déployée sur une infrastructure souveraine, sécurisée et hautement disponible. </p>
          <div class="integration__visuals">
            <figure class="img-parallax"><img class="integration__visual integration__visual--smartphone"
                :src="smartphoneImage" :alt="smartphoneImageAlt" loading="lazy" decoding="async"></figure>
          </div>

          <ul class="section-list integration__services-list">
            <li v-for="integration in integrations" :key="integration.key"
              class="section-list__item integration__solution-item">
              <span class="section-list__item-icon integration__solution-icon" aria-hidden="true">
                <i :class="[integration.iconFamily, `fa-${integration.icon}`]"></i>
              </span>
              <h3 class="integration__solution-title">{{ integration.title }}</h3>
              <p class="integration__solution-text">{{ integration.text }}</p>
            </li>
          </ul>
        </div>
      </section>
      <!-- 06 DÉFIS SECTION -->
      <section id="section-defis" class="section defis mainsection__defis">
        <img class="defis__visual" :src="nostressImage" :alt="nostressImageAlt" loading="lazy" decoding="async">
        <figure class="img-parallax"><img class="defis__shield" :src="serveursecuriteshieldcheckImage"
            :alt="serveursecuriteshieldcheckImageAlt" loading="lazy" decoding="async">
        </figure>
        <div class="section-content defis__content">
          <span class="defis__number section__number">06</span>
          <div class="defis__cards">
            <article v-for="defi in defis" :key="defi.key" class="defis__card">
              <header class="defis__card-header">
                <p class="defis__eyebrow">{{ defi.subtitle }}</p>
                <h2 class="defis__title">
                  {{ defi.title }}
                  <span class="defis__title-accent">{{ defi.titleAccent }}</span>
                </h2>
              </header>
              <ul class="defis__items">
                <li v-for="item in defi.items" :key="item" class="defis__item">
                  <span class="defis__item-icon" aria-hidden="true">
                    <i class="fa-solid fa-circle-check"></i>
                  </span>
                  <p class="defis__item-text">{{ item }}</p>
                </li>
              </ul>
            </article>
          </div>
        </div>
      </section>
      <!-- 07 BÉNÉFICES SECTION -->
      <section id="section-benefices" class="section benefices mainsection__benefices">
        <div class="section-content benefices__content">
          <header class="section_header benefices__header">
            <span class="benefices__number section__number">07</span>
            <h2 class="benefices__title">Plus de <span class="benefices__title-accent">performance.</span>Moins de
              risques.</h2>
            <h3>Vos bénéfices</h3>
          </header>
          <p>SmartWork vous accompagne dans l’adoption d’une IA souveraine&nbsp;: sécurisée, performante et conforme aux normes françaises et européennes — pour que l’innovation rime avec protection de vos données.</p>
          <ul class="section-list benefices__services-list">
            <li v-for="benefice in benefices" :key="benefice.key" class="section-list__item benefices__solution-item">
              <span class="section-list__item-icon benefices__solution-icon" aria-hidden="true">
                <i :class="[benefice.iconFamily, `fa-${benefice.icon}`]"></i>
              </span>
              <h3 class="benefices__solution-title">{{ benefice.title }}</h3>
              <p class="benefices__solution-text">{{ benefice.text }}</p>
            </li>
          </ul>
        </div>
      </section>
      <!-- 08 ÉCOLOGIE SECTION -->
      <section id="section-environnement" class="section environnement mainsection__environnement">
        <img class="environnement__plant" :src="serveurecoplanteImage" :alt="serveurecoplanteImageAlt" width="236" height="236" loading="lazy" decoding="async">
        <div class="section-content environnement__content">
          <header class="section_header environnement__header">
            <span class="environnement__number section__number">08</span>
            <p class="environnement__eyebrow">Environnement</p>
            <h2 class="environnement__title">
              Une infrastructure responsable, pensée pour la
              <span class="environnement__title-accent">performance</span>.
            </h2>
            <p class="environnement__text">
              Votre IA souveraine s’appuie sur une infrastructure française sobre et responsable&nbsp;: énergie
              renouvelable, matériel réemployé et efficacité optimisée, sans compromis sur la performance.
            </p>
          </header>

          <ul class="environnement__stats">
            <li v-for="stat in environnementStats" :key="stat.key" class="environnement__stat">
              <strong class="environnement__stat-value">{{ stat.value }}</strong>
              <p class="environnement__stat-text">{{ stat.text }}</p>
            </li>
          </ul>
        </div>
        <img class="environnement__leaf" :src="serveurecofeuilleImage" :alt="serveurecofeuilleImageAlt" loading="lazy" decoding="async">
      </section>
      <!-- 09 CONTACT SECTION -->
      <section id="section-contact" class="section contact mainsection__contact">
        <div class="section-content contact__content">
          <div class="contact__intro">
            <p class="contact__eyebrow">Contactez-nous</p>
            <h2 class="contact__title">
              Parlons de
              <span class="contact__title-accent">vos enjeux.</span>
              Moins de risques.
            </h2>
            <p class="contact__lead">
              Un projet d’IA souveraine, une question, un cas d’usage à explorer ? Notre équipe vous répond rapidement.
            </p>

            <ul class="contact__channels" aria-label="Coordonnées SmartWork">
              <li class="contact__channel">
                <span class="contact__channel-icon" aria-hidden="true">
                  <i class="fa-solid fa-mobile-screen-button"></i>
                </span>
                <a href="tel:+33278776792">+33 2 78 77 67 92</a>
              </li>
              <li class="contact__channel">
                <span class="contact__channel-icon" aria-hidden="true">
                  <i class="fa-solid fa-at"></i>
                </span>
                <a href="mailto:contact@smartwork.fr">contact@smartwork.fr</a>
              </li>
            </ul>
          </div>

          <img class="contact__visual" :src="contactImage" :alt="contactImageAlt" width="279" height="264" loading="lazy" decoding="async">

          <form class="contact__form" @submit.prevent="handleContactSubmit">
            <label class="contact__label" for="contact-name">Nom</label>
            <input id="contact-name" v-model.trim="contactForm.name" class="contact__field" type="text"
              placeholder="Nom" autocomplete="name" maxlength="120" required>

            <label class="contact__label" for="contact-email">Email</label>
            <input id="contact-email" v-model.trim="contactForm.email" class="contact__field" type="email"
              placeholder="Email" autocomplete="email" maxlength="254" required>

            <label class="contact__label" for="contact-phone">Téléphone</label>
            <input id="contact-phone" v-model.trim="contactForm.phone" class="contact__field" type="tel"
              placeholder="Téléphone" autocomplete="tel" inputmode="tel" maxlength="30">

            <label class="contact__label" for="contact-company">Entreprise</label>
            <input id="contact-company" v-model.trim="contactForm.company" class="contact__field" type="text"
              placeholder="Entreprise" autocomplete="organization" maxlength="160">

            <label class="contact__label" for="contact-message">Décrivez-nous votre projet ou vos besoins</label>
            <textarea id="contact-message" v-model.trim="contactForm.message"
              class="contact__field contact__field--textarea" placeholder="Décrivez-nous votre projet ou vos besoins..."
              rows="6" maxlength="5000" required></textarea>

            <label class="contact__consent">
              <input v-model="contactForm.consent" type="checkbox" required>
              <span>
                En cochant cette case, je confirme que je suis d’accord avec les conditions générales d’utilisation de
                mes
                données consultables ici.
              </span>
            </label>

            <label class="contact__trap" for="contact-gotcha">Ne pas remplir</label>
            <input id="contact-gotcha" v-model="contactForm.gotcha" class="contact__trap" type="text" tabindex="-1"
              autocomplete="off">

            <button class="contact__submit" type="submit" :disabled="isContactSubmitting">
              <span>{{ isContactSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande' }}</span>
              <i class="fa-solid fa-arrow-right" aria-hidden="true"></i>
            </button>

            <p v-if="contactFeedback" class="contact__feedback" :class="`contact__feedback--${contactStatus}`"
              aria-live="polite">
              {{ contactFeedback }}
            </p>
          </form>
          <aside class="contact__appointment">
            <span class="contact__appointment-icon" aria-hidden="true">
              <i class="fa-solid fa-headset"></i>
            </span>
            <p class="contact__appointment-text">
              Besoin d’un échange immédiat ? Nos experts sont disponibles pour un premier échange gratuit de 30 minutes.
            </p>

            <form class="contact__booking" @submit.prevent="handleBookingSubmit">
              <p class="contact__booking-title">Demander un créneau</p>

              <label class="contact__label" for="booking-name">Nom</label>
              <input id="booking-name" v-model.trim="bookingForm.name" class="contact__field" type="text"
                placeholder="Nom" autocomplete="name" maxlength="120" required>

              <label class="contact__label" for="booking-email">Email</label>
              <input id="booking-email" v-model.trim="bookingForm.email" class="contact__field" type="email"
                placeholder="Email" autocomplete="email" maxlength="254" required>

              <label class="contact__label" for="booking-phone">Téléphone</label>
              <input id="booking-phone" v-model.trim="bookingForm.phone" class="contact__field" type="tel"
                placeholder="Téléphone" autocomplete="tel" inputmode="tel" maxlength="30" required>

              <label class="contact__label" for="booking-company">Entreprise</label>
              <input id="booking-company" v-model.trim="bookingForm.company" class="contact__field" type="text"
                placeholder="Entreprise" autocomplete="organization" maxlength="160">

              <label class="contact__label" for="booking-date">Jour souhaité</label>
              <select id="booking-date" v-model="bookingForm.date" class="contact__field" required>
                <option value="" disabled>Choisir un jour…</option>
                <option v-for="day in bookingDays" :key="day.date" :value="day.date">
                  {{ formatBookingDate(day.date) }}
                </option>
              </select>

              <div v-if="bookingForm.date" class="contact__booking-slots">
                <button v-for="slot in bookingSlots" :key="slot" type="button" class="contact__slot"
                  :class="{ 'contact__slot--active': bookingForm.slot === slot }"
                  @click="bookingForm.slot = slot">
                  {{ slot }}
                </button>
              </div>

              <label class="contact__label" for="booking-duration">Durée</label>
              <select id="booking-duration" v-model.number="bookingForm.duration" class="contact__field">
                <option v-for="d in bookingDurations" :key="d.value" :value="d.value">{{ d.label }}</option>
              </select>

              <label class="contact__trap" for="booking-gotcha">Ne pas remplir</label>
              <input id="booking-gotcha" v-model="bookingForm.gotcha" class="contact__trap" type="text" tabindex="-1"
                autocomplete="off">

              <button class="contact__submit contact__submit--booking" type="submit" :disabled="isBookingSubmitting || !bookingForm.slot">
                <span>{{ isBookingSubmitting ? 'Envoi en cours...' : 'Demander ce créneau' }}</span>
                <i class="fa-solid fa-arrow-right" aria-hidden="true"></i>
              </button>

              <p v-if="bookingFeedback" class="contact__feedback" :class="`contact__feedback--${bookingStatus}`"
                aria-live="polite">
                {{ bookingFeedback }}
              </p>
            </form>
          </aside>
        </div>
      </section>
    </main>
    <footer class="site-footer">
      <div class="site-footer__inner">
        <div class="site-footer__brand-row">
          <a class="site-footer__brand" href="/#section-hero" aria-label="SmartWork">
            <span>Smart</span><span class="site-footer__brand-accent">W</span><span>ork</span>
          </a>
          <p class="site-footer__tagline">
            Votre partenaire de confiance pour adopter l’IA en toute sécurité, souveraineté et confidentialité.
          </p>
        </div>

        <div class="site-footer__bottom">
          <p class="site-footer__copyright">© 2026 SmartWork. Tous droits réservés.</p>
          <nav class="site-footer__nav" aria-label="Liens légaux">
            <a v-for="link in legalPageLinks" :key="link.path" :href="link.path"
              :aria-current="isLegalLinkActive(link.path) ? 'page' : undefined">
              {{ link.label }}
            </a>
          </nav>
        </div>
      </div>
    </footer>

    <nav class="primary-nav" aria-label="Navigation principale">
      <a v-for="item in primaryNavigationItems" :key="item.targetId" class="primary-nav__link"
        :class="[item.variant ? `primary-nav__link--${item.variant}` : null, { 'primary-nav__link--active': activeSectionId === item.targetId }]"
        :href="item.href" :aria-label="item.label"
        :aria-current="activeSectionId === item.targetId ? 'location' : undefined">
        <span class="primary-nav__marker">
          <i v-if="item.icon" :class="[item.iconFamily, `fa-${item.icon}`]" aria-hidden="true"></i>
          <span v-else class="primary-nav__text">{{ item.shortLabel }}</span>
        </span>
      </a>
    </nav>
  </div>
</template>
